import { ReactNode, useState } from 'react';

import { TenantDTO } from '@recurrency/core-api-schema/dist/tenants/tenantDTO';
import { fuzzyFilter } from 'fuzzbunny';
import { useDebounce } from 'use-debounce/lib';

import { usePromise } from 'hooks/usePromise';

import { splitIdNameStr } from 'utils/formatting';
import { searchIndexForFacetValues } from 'utils/search/search';
import { FacetValues, SearchRequest } from 'utils/search/types';

import { highlightedLabel, MultiSelectOption, UseAsyncMultiSelectProps } from './types';
import { UseAsyncSelectProps } from './useAsyncSelectProps';

/**
 * useAsyncSelectProps has value = `{id}: {name}` but we want value = `{id}` and label = `{id}: {name}`
 * `{id}: name` values were a hack for Q/O entry, and we don't want to further propagate that pattern
 */
export function convertToMultiSelectProps(
  selectProps: UseAsyncSelectProps,
  {
    valueKey = 'foreignId',
    labelKey = 'value',
  }: { valueKey?: 'foreignId' | 'uid' | 'idName'; labelKey?: 'label' | 'value' | 'uid' } = {},
): UseAsyncMultiSelectProps {
  return {
    options: selectProps.options
      .filter((opt) => !opt.disabled)
      .map(({ uid, value, label }) => {
        const optionValue =
          valueKey === 'uid' && uid ? uid : valueKey === 'foreignId' ? splitIdNameStr(value).foreignId : value;

        return {
          label: labelKey === 'label' ? label : value,
          value: optionValue,
        };
      }),
    totalCount: selectProps.totalCount,
    isLoading: selectProps.isLoading,
    searchQuery: selectProps.searchQuery,
    setSearchQuery: selectProps.setSearchQuery,
  };
}

export interface PillFilterOption extends Omit<MultiSelectOption, 'value'> {
  value: string | string[];
}

export interface FacetValueOption {
  label: ReactNode;
  value: string;
  count: number;
}

export function getSearchFacetMultiSelectOptions({
  facetField,
  searchRequest,
  labelFormatFn,
  sortBy,
  staticOptions,
}: {
  facetField: string;
  searchRequest: SearchRequest;
  labelFormatFn?: (value: string) => ReactNode;
  sortBy?: 'alpha' | 'count' | 'none';
  staticOptions?: Array<{ label?: ReactNode; value: string }>;
}): (isOpen: boolean) => UseAsyncMultiSelectProps {
  return function useSearchIndexFacetOptions(isOpen: boolean) {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

    // omit filter on facetField so we get all values
    if (searchRequest.filters?.[facetField]) {
      searchRequest = { ...searchRequest, filters: { ...searchRequest.filters } };
      delete searchRequest.filters![facetField];
    }

    // if searchQuery changes, search for matching options
    const { data: filteredOptions = [], isLoading } = usePromise(
      () =>
        isOpen === false
          ? Promise.resolve([])
          : searchIndexForFacetValues(facetField, debouncedSearchQuery, searchRequest).then((facetValues) =>
              mergeFacetValuesWithStaticOptions({ facetValues, staticOptions, labelFormatFn, sortBy }),
            ),
      [],
      { cacheKey: JSON.stringify({ facetField, isOpen, debouncedSearchQuery, ...searchRequest }) },
    );

    const filteredAndHighlightedOptions = fuzzyFilterAndHighlightOptions(filteredOptions, debouncedSearchQuery);

    return {
      isLoading,
      options: filteredAndHighlightedOptions,
      // TODO: ideally we should return the total count of the facet values, but we don't currently return that from the API
      totalCount: filteredAndHighlightedOptions.length,
      searchQuery,
      setSearchQuery,
    };
  };
}

export function mergeFacetValuesWithStaticOptions({
  facetValues,
  staticOptions,
  labelFormatFn,
  sortBy,
}: {
  facetValues: FacetValues;
  staticOptions?: Array<{ label?: ReactNode; value: string }>;
  labelFormatFn?: (value: string) => ReactNode;
  sortBy?: 'alpha' | 'count' | 'none';
}): FacetValueOption[] {
  let facetOptions: FacetValueOption[] = [];

  if (staticOptions) {
    // add counts from response to static options
    facetOptions = staticOptions.map(({ label, value }) => ({
      label: labelFormatFn ? labelFormatFn(value) : label ?? value,
      value,
      count: facetValues.find((v) => v.value === value)?.count ?? 0,
    }));
  } else {
    facetOptions = facetValues.map(({ value, count }) => ({
      label: labelFormatFn ? labelFormatFn(value) : value,
      value,
      count,
    }));
  }

  if (sortBy !== 'none') {
    facetOptions.sort(sortBy === 'alpha' ? (a, b) => a.value.localeCompare(b.value) : (a, b) => b.count - a.count);
  }

  return facetOptions;
}

export function getPillFacetOptions({
  facetValues,
  labelFormatFn,
  staticOptions,
}: {
  facetValues: FacetValues;
  labelFormatFn?: (value: string) => string;
  staticOptions?: PillFilterOption[];
}) {
  return function useSearchIndexFacetOptions() {
    // options are inferred if static options not provided
    const options = staticOptions
      ? staticOptions.map((option) => {
          const optionsArray = Array.isArray(option.value) ? option.value : [option.value];
          const count = facetValues
            .filter((v) => optionsArray.includes(v.value))
            .reduce((total, curr) => total + curr.count, 0);

          return {
            label: option.label,
            value: option.value,
            // update counts for static options
            count,
          };
        })
      : facetValues.map((facetValue) => ({
          label: labelFormatFn ? labelFormatFn(facetValue.value) : facetValue.value,
          value: facetValue.value,
          count: facetValue.count,
        }));

    return {
      options,
      totalCount: options.length,
    };
  };
}

export function useTenantSlugSelectProps(tenants: TenantDTO[]): UseAsyncMultiSelectProps {
  const [searchQuery, setSearchQuery] = useState('');
  const options = tenants.map((tenant) => ({
    label: tenant.name,
    value: tenant.slug,
  }));

  return {
    searchQuery,
    setSearchQuery,
    options: fuzzyFilterAndHighlightOptions(options, searchQuery),
  };
}

export function fuzzyFilterAndHighlightOptions(options: MultiSelectOption[], searchQuery: string): MultiSelectOption[] {
  if (!searchQuery) return options;

  const filteredOptions = fuzzyFilter(options, searchQuery, { fields: ['label', 'value'] });
  return filteredOptions.map((option) => ({
    label: option.highlights.label ? highlightedLabel(option.highlights.label) : option.item.label,
    value: option.item.value,
    count: option.item.count,
  }));
}
